/* eslint-disable camelcase */
import { getId } from '@/helpers';

// COMMON DATA AND FUNCTIONS

const evaluationInAsd = {
  states: { name: '' },
  status: '',
  comments_user: '',
};

function getFinalGradeRank(proposal) {
  return proposal.final_grade.states.name === 'submitted'
    ? proposal.final_grade.rank : '';
}

function getManagementMark(proposal) {
  return proposal.management_evaluation.states.name === 'submitted'
    ? proposal.management_evaluation.acceptance_mark : '';
}

function setRankInFinalGrade(proposals) {
  return proposals.map((proposal) => {
    if (proposal.final_grade === undefined) {
      return {
        ...proposal,
        final_grade: { rank: '' },
      };
    }
    const { final_grade = {} } = proposal;
    final_grade.rank = getFinalGradeRank(proposal);
    return {
      ...proposal,
      final_grade,
    };
  });
}

function setAcceptanceMarkInManagementEvaluation(proposals) {
  return proposals.map((proposal) => {
    if (proposal.management_evaluation === undefined) {
      return {
        ...proposal,
        management_evaluation: { acceptance_mark: '' },
      };
    }
    const { management_evaluation = {} } = proposal;
    management_evaluation.acceptance_mark = getManagementMark(proposal);
    return {
      ...proposal,
      management_evaluation,
    };
  });
}

function setTechnicalEvaluationData(proposals) {
  return proposals.map((proposal) => {
    const { technical_evaluation = {} } = proposal;
    technical_evaluation.comments = technical_evaluation.states?.name === 'submitted'
      ? technical_evaluation.comments
      : '';
    return { ...proposal, technical_evaluation };
  });
}

// SAFETY_EVALUATION DATA AND FUNCTIONS

const additionalSampleEvaluationFieldsSafety = [
  {
    key: 'document_specification.document_id',
    label: 'ID',
    sortable: true,
    class: 'custom-column-id-width',
  },
  {
    key: 'title',
    label: 'Title',
    sortable: true,
    thStyle: { width: '15%' },
  },
  {
    key: 'author',
    label: 'Author',
    sortable: true,
    tdClass: 'preLine',
  },
  {
    key: 'samples[0].safety_evaluation.status',
    label: 'Sample status',
    tdClass: 'text-center',
    sortable: true,
    trafficLights: true,
    trafficLightsOptionsType: 'trafficLightsOptionsSafety',
  },
  {
    key: 'additional_sample_declaration.safety_evaluation.status',
    label: 'ASD sample status',
    tdClass: 'text-center',
    sortable: true,
    trafficLights: true,
    trafficLightsOptionsType: 'trafficLightsOptionsSafety',
  },
  {
    key: 'additional_sample_declaration.safety_evaluation.states.name',
    label: 'ASD sample evaluation status',
    sortable: true,
  },
  {
    key: 'additional_sample_declaration.safety_evaluation.comments_user',
    label: 'ASD sample evaluation comments',
  },
  {
    key: 'actions',
    thStyle: { width: '10%' },
  },
];

const samplesInCericSafety = [{
  risk: '- (CERIC)',
  sample_safety: ['- (CERIC)'],
  safety_evaluation: { status: '- (CERIC)', states: { name: '- (CERIC)' } },
}];

function setSafetyEvaluationInSample(sample) {
  if (sample.safety_evaluation === undefined) {
    return { ...sample, safety_evaluation: { states: {}, status: '' } };
  }
  if (sample.safety_evaluation.status === undefined
    || sample.safety_evaluation.status === null) {
    const { safety_evaluation = {} } = sample;
    safety_evaluation.status = '';
    return { ...sample, safety_evaluation };
  }
  return sample;
}

function setSafetyEvaluationInSamples(proposals, authors) {
  return proposals.map((proposal) => {
    const proposalId = getId(proposal);
    // if proposal has samples i.e. is not CERIC
    if (Array.isArray(proposal.samples)) {
      return {
        ...proposal,
        samples: proposal.samples.map((sample) => setSafetyEvaluationInSample(sample)),
        author: `${authors[proposalId].name}\n${authors[proposalId].email}`,
      };
    }
    // if proposal is CERIC
    return {
      ...proposal,
      samples: samplesInCericSafety,
      author: `${authors[proposalId].name}\n${authors[proposalId].email}`,
    };
  });
}

function setSafetyEvaluationInAsd(proposals) {
  return proposals.map((proposal) => {
    if (proposal.additional_sample_declaration === undefined) {
      return {
        ...proposal,
        additional_sample_declaration: { safety_evaluation: evaluationInAsd },
      };
    }
    if (proposal.additional_sample_declaration.safety_evaluation === undefined) {
      const { additional_sample_declaration = {} } = proposal;
      additional_sample_declaration.safety_evaluation = evaluationInAsd;
      return {
        ...proposal,
        additional_sample_declaration,
      };
    }
    return proposal;
  });
}

async function setDataInProposalsSafety(responseData, infrastructure) {
  let proposals = responseData.documents;
  const { authors } = responseData;
  proposals = setSafetyEvaluationInSamples(proposals, authors);
  proposals = setTechnicalEvaluationData(proposals);
  if (infrastructure === 'cryoem') {
    proposals = setRankInFinalGrade(proposals);
  } else {
    proposals = setAcceptanceMarkInManagementEvaluation(proposals);
    proposals = setSafetyEvaluationInAsd(proposals);
  }
  return proposals;
}

// RADIATION_EVALUATION DATA AND FUNCTIONS

const additionalSampleEvaluationFieldsRadiation = [
  {
    key: 'document_specification.document_id',
    label: 'ID',
    sortable: true,
    class: 'custom-column-id-width',
  },
  {
    key: 'title',
    label: 'Title',
    sortable: true,
    thStyle: { width: '15%' },
  },
  {
    key: 'author',
    label: 'Author',
    sortable: true,
    tdClass: 'preLine',
  },
  {
    key: 'samples[0].radiation_evaluation.status',
    label: 'Sample status',
    tdClass: 'text-center',
    sortable: true,
    trafficLights: true,
    trafficLightsOptionsType: 'trafficLightsOptionsSafety',
  },
  {
    key: 'additional_sample_declaration.radiation_evaluation.status',
    label: 'ASD sample status',
    tdClass: 'text-center',
    sortable: true,
    trafficLights: true,
    trafficLightsOptionsType: 'trafficLightsOptionsSafety',
  },
  {
    key: 'additional_sample_declaration.radiation_evaluation.states.name',
    label: 'ASD sample evaluation status',
    sortable: true,
  },
  {
    key: 'additional_sample_declaration.radiation_evaluation.comments_user',
    label: 'ASD sample evaluation comments',
  },
  {
    key: 'actions',
    thStyle: { width: '10%' },
  },
];

const samplesInCericRadiation = [{
  risk: '- (CERIC)',
  sample_safety: ['- (CERIC)'],
  sample_safety_radioactive: false,
  radiation_evaluation: { status: '- (CERIC)', states: { name: '- (CERIC)' } },
}];

function setRadiationEvaluationAndIsRadioactiveInSample(sample) {
  const { sample_safety = {} } = sample;
  const isRadioactive = Array.isArray(sample_safety) ? sample_safety.includes('Radioactive') : false;
  if (sample.radiation_evaluation === undefined) {
    return { ...sample, sample_safety_radioactive: isRadioactive, radiation_evaluation: { states: {}, status: '' } };
  }
  if (sample.radiation_evaluation.status === undefined
    || sample.radiation_evaluation.status === null) {
    const { radiation_evaluation = {} } = sample;
    radiation_evaluation.status = '';
    return { ...sample, sample_safety_radioactive: isRadioactive, radiation_evaluation };
  }
  return { ...sample, sample_safety_radioactive: isRadioactive };
}

function setRadiationEvaluationInSample(sample, infrastructure) {
  if (infrastructure === 'cryoem') {
    if (sample.radiation_evaluation === undefined) {
      return { ...sample, radiation_evaluation: { states: {}, status: '' } };
    }
    if (sample.radiation_evaluation.status === undefined
      || sample.radiation_evaluation.status === null) {
      const { radiation_evaluation = {} } = sample;
      radiation_evaluation.status = '';
      return { ...sample, radiation_evaluation };
    }
    return sample;
  }
  return setRadiationEvaluationAndIsRadioactiveInSample(sample);
}

function setRadiationEvaluationInSamples(proposals, authors, infrastructure) {
  return proposals.map((proposal) => {
    const proposalId = getId(proposal);
    // if proposal has samples i.e. is not CERIC
    if (Array.isArray(proposal.samples)) {
      return {
        ...proposal,
        samples: proposal.samples.map((sample) => setRadiationEvaluationInSample(sample, infrastructure)),
        author: `${authors[proposalId].name}\n${authors[proposalId].email}`,
      };
    }
    // if proposal is CERIC
    return {
      ...proposal,
      samples: samplesInCericRadiation,
      author: `${authors[proposalId].name}\n${authors[proposalId].email}`,
    };
  });
}

function setRadiationEvaluationInAsd(proposals) {
  return proposals.map((proposal) => {
    if (proposal.additional_sample_declaration === undefined) {
      return {
        ...proposal,
        additional_sample_declaration: { radiation_evaluation: evaluationInAsd },
      };
    }
    if (proposal.additional_sample_declaration.radiation_evaluation === undefined) {
      const { additional_sample_declaration = {} } = proposal;
      additional_sample_declaration.radiation_evaluation = evaluationInAsd;
      return {
        ...proposal,
        additional_sample_declaration,
      };
    }
    return proposal;
  });
}

async function setDataInProposalsRadiation(responseData, infrastructure) {
  let proposals = responseData.documents;
  const { authors } = responseData;
  proposals = setRadiationEvaluationInSamples(proposals, authors, infrastructure);
  proposals = setTechnicalEvaluationData(proposals);
  if (infrastructure === 'cryoem') {
    proposals = setRankInFinalGrade(proposals);
  } else {
    proposals = setAcceptanceMarkInManagementEvaluation(proposals);
    proposals = setRadiationEvaluationInAsd(proposals);
  }
  return proposals;
}

export {
  additionalSampleEvaluationFieldsSafety,
  additionalSampleEvaluationFieldsRadiation,
  setDataInProposalsSafety,
  setDataInProposalsRadiation,
  setSafetyEvaluationInSamples,
  setRadiationEvaluationInSamples,
};
